import { useState, useRef, useCallback, useEffect } from 'react';

const DRAG_THRESHOLD = 5;

/**
 * Drag a card onto another card to reassign its manager.
 *
 * Follows the convention every graph editor uses: dragging an OBJECT moves the
 * object, dragging the BACKGROUND moves the view. Cards therefore opt out of
 * panning (`data-orgchart-node`), and `usePanZoom` only pans from empty canvas.
 *
 * The drop target is resolved with `document.elementFromPoint` rather than by
 * hit-testing layout coordinates: the canvas is scaled and translated, so screen
 * position is the only thing both the pointer and the DOM agree on.
 *
 * Nothing is written here — a drop asks `onDrop(dragged, target)`, which opens a
 * confirmation. A reorganisation should never be one slip of the wrist away.
 */
const useNodeDrag = ({ enabled, onDrop, isValidTarget }) => {
  const [dragging, setDragging] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [ghostPosition, setGhostPosition] = useState(null);

  const pending = useRef(null);
  const draggedRef = useRef(false);

  const handlersFor = useCallback((node) => {
    if (!enabled || node.isCompanyRoot) return {};

    return {
      'data-orgchart-node': true,
      'data-orgchart-node-id': node.id,
      onPointerDown: (event) => {
        if (event.button !== 0) return;
        // Controls on the card own their own clicks.
        if (event.target.closest('[data-orgchart-nopan]')) return;
        if (event.target.closest('[data-orgchart-open]')) return;

        draggedRef.current = false;
        pending.current = { node, startX: event.clientX, startY: event.clientY, active: false };
      },
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    const resolveTarget = (clientX, clientY) => {
      const element = document.elementFromPoint(clientX, clientY);
      const card = element?.closest('[data-orgchart-node-id]');
      if (!card) return null;

      const id = Number(card.getAttribute('data-orgchart-node-id'));
      return Number.isNaN(id) ? null : id;
    };

    const onMove = (event) => {
      const state = pending.current;
      if (!state) return;

      const distance = Math.abs(event.clientX - state.startX) + Math.abs(event.clientY - state.startY);
      if (!state.active && distance < DRAG_THRESHOLD) return;

      if (!state.active) {
        state.active = true;
        draggedRef.current = true;
        setDragging(state.node);
      }

      setGhostPosition({ x: event.clientX, y: event.clientY });

      const targetId = resolveTarget(event.clientX, event.clientY);
      setDropTargetId(targetId !== null && isValidTarget(state.node, targetId) ? targetId : null);
    };

    const onUp = (event) => {
      const state = pending.current;
      pending.current = null;

      if (!state?.active) {
        setDragging(null);
        setDropTargetId(null);
        setGhostPosition(null);
        return;
      }

      const targetId = resolveTarget(event.clientX, event.clientY);
      const valid = targetId !== null && isValidTarget(state.node, targetId);

      setDragging(null);
      setDropTargetId(null);
      setGhostPosition(null);

      if (valid) onDrop(state.node, targetId);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [enabled, onDrop, isValidTarget]);

  return {
    dragging,
    dropTargetId,
    ghostPosition,
    handlersFor,
    /** True when the gesture that just ended was a card drag, so the click that
     *  follows it must not also select the card. */
    consumedClick: useCallback(() => draggedRef.current, []),
  };
};

export default useNodeDrag;
